import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import CryptoJS from 'crypto-js';
import { parseJwt } from '@marqvision/shared';
import { Auth } from '~/swagger/Auth';
import { AuthProtocolLoginResponse } from '~/swagger/data-contracts';
import { queryClient } from '..';
import { http } from './base';

const QueryKey = 'auth';
export const AuthApi = new Auth(http);

const STORE_SECRET = process.env.REACT_APP_STORE_SECRET || '';

const UserInfoLocalStorageIdentifier = '_mqfl_ui';

type AuthData = AuthProtocolLoginResponse & {
  email: string;
  userId: number;
};

type AdminEmail = {
  adminEmail?: string;
};

type ClientViewData = { isClientView: boolean } & AdminEmail;

const parseToken = (data: string): AuthData & AdminEmail => {
  const auth = JSON.parse(
    CryptoJS.AES.decrypt(data, STORE_SECRET).toString(CryptoJS.enc.Utf8)
  ) as AuthProtocolLoginResponse & AdminEmail;
  const { email, userId, adminEmail } = parseJwt<{ email: string; userId: number; adminEmail?: string }>(
    auth.accessToken
  )!;

  return {
    ...auth,
    email,
    userId,
    adminEmail,
  };
};

const getAuth = (): (AuthData & ClientViewData) | null => {
  try {
    const { data, isClientView }: { data: string | null; isClientView: boolean } = (() => {
      const sessionAuth = sessionStorage.getItem(UserInfoLocalStorageIdentifier);
      if (sessionAuth) {
        return {
          data: sessionAuth,
          isClientView: true,
        };
      } else {
        return {
          data: localStorage.getItem(UserInfoLocalStorageIdentifier),
          isClientView: false,
        };
      }
    })();
    if (data) {
      const parseAuth = parseToken(data);

      return {
        ...parseAuth,
        isClientView,
      };
    }
    return null;
  } catch (e) {
    clearAuth();
    throw new Error('Failed to load user info');
  }
};

const setAuth = (authData: AuthProtocolLoginResponse, isClientView?: boolean) => {
  queryClient.setQueryData([QueryKey], authData);
  const encryptData = CryptoJS.AES.encrypt(JSON.stringify(authData), STORE_SECRET).toString();
  if (isClientView) {
    sessionStorage.setItem(UserInfoLocalStorageIdentifier, encryptData);
  } else {
    localStorage.setItem(UserInfoLocalStorageIdentifier, encryptData);
  }
};

export const clearAuth = (): void => {
  queryClient.setQueryData([QueryKey], null);
  localStorage.removeItem(UserInfoLocalStorageIdentifier);
  sessionStorage.removeItem(UserInfoLocalStorageIdentifier);
};

export const refreshAuth = async (refreshToken: string) => {
  const { data } = await AuthApi.refreshCreate({
    refreshToken,
  });

  const { accessToken } = data;

  setAuth({ accessToken, refreshToken }, !!sessionStorage.getItem(UserInfoLocalStorageIdentifier));

  return accessToken;
};

export const useAuth = (): UseQueryResult<(AuthData & ClientViewData) | null> => useQuery([QueryKey], getAuth);

export const useLoginMutation = () => {
  const queryClient = useQueryClient();

  return useMutation(AuthApi.loginCreate, {
    onSuccess: (response) => {
      setAuth(response.data);
      queryClient.clear();
    },
  });
};

export const useSignupMutation = () =>
  useMutation(AuthApi.signupCreate, {
    onSuccess: (response) => {
      setAuth(response.data);
    },
  });

export const usePasswordResetMutation = () =>
  useMutation(AuthApi.findResetCreate, {
    onSuccess: (response) => {
      setAuth(response.data);
    },
  });

export const useCheckSignUp = () => useMutation(AuthApi.emailVerifyCreate);

export const useSendSignupVerify = () => useMutation(AuthApi.signupVerifySendCreate);

export const useCheckSignupVerify = () => useMutation(AuthApi.signupVerifyCheckCreate);

export const useSetClientViewAuthMutation = () =>
  useMutation({
    onMutate: (token: string) => {
      setAuth(JSON.parse(token), true);
    },
  });
